import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { TableCell, TableRow } from '@mui/material';

import { formatDate } from '../../../helpers/formatDate';
import { DeductionData } from '../../../types';
import { numberToUSDCurrency } from '../../../helpers/numberToUSDCurrency';
import { palette } from '../../../palette';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { StatusChip } from '../../../components/StatusChip';
import LienRecoveryPopup, { LienRecoveryLien } from '../../../components/LienRecoveryPopup';
import { DeductionStatusMap, DeductionTypeMap } from '../SettlementsView/constants';
import { DeductionMenuOption } from '../types';

import DeductionOptionsMenu from './DeductionOptionsMenu';

interface DeductionsTableDataRowProps {
  tableData: DeductionData[];
  onRecoverySaved?: () => void;
}

const DeductionsTableDataRow = ({ tableData, onRecoverySaved }: DeductionsTableDataRowProps) => {
  const navigate = useNavigate();
  const canEdit = useHasPermission('Payments', 'Edit');

  const [recoveryLien, setRecoveryLien] = useState<LienRecoveryLien | null>(null);

  const onTableRowClick = (id: number) => {
    navigate(`/payments/deduction/${id}/home`);
  };

  // Recoveries apply only to paid Lien and Service Provider deductions
  const getMenuOptions = (deduction: DeductionData) =>
    canEdit &&
    deduction.mappedDeductionStatus === 'Paid' &&
    (deduction.type === 'Lien' || deduction.type === 'SERVICE_PROVIDER')
      ? [DeductionMenuOption.MarkAsRecovery]
      : [];

  const onMenuOptionClick = (deduction: DeductionData, option: DeductionMenuOption) => {
    if (option === DeductionMenuOption.MarkAsRecovery) {
      setRecoveryLien({
        id: String(deduction.id),
        providerName: deduction.vendor?.name || '-',
        paidAmount: deduction.amount,
        paidAt: deduction.paidAt,
      });
    }
  };

  const formattedFirmsList =
    tableData &&
    tableData.map((deduction: DeductionData) => ({
      id: deduction.id,
      deduction,
      rowFields: {
        // name: deduction.case.name,
        referenceId: deduction.case.referenceId,
        plaintiff: `${deduction.plaintiff.firstName} ${deduction.plaintiff.middleName || ''} ${
          deduction.plaintiff.lastName
        }`,
        // defendant: deduction.defendant?.name || '-',
        // entryDate: formatDate(deduction.createdAt as string),
        updatedAt: formatDate(deduction.updatedAt as string),
        type: DeductionTypeMap[deduction.type] || deduction.type,
        // amount: `$${deduction.amount}`,
        vendor: deduction.vendor?.name || '-',
        amount: numberToUSDCurrency(deduction.amount),
        status: deduction.mappedDeductionStatus,
        paidAt: deduction.paidAt ? formatDate(deduction.paidAt as string) : '-',
      },
    }));

  return (
    <>
      {formattedFirmsList.map((tableItem, idx) => (
        <TableRow key={idx} onClick={() => onTableRowClick(tableItem.id)}>
          {Object.entries(tableItem.rowFields).map(([key, value], idx) => (
            <TableCell key={idx}>
              {key === 'status' ? (
                (tableItem.deduction.recoveredAmount ?? 0) > 0 ? (
                  <StatusChip
                    label={`Recovered ${numberToUSDCurrency(tableItem.deduction.recoveredAmount as number)}`}
                    color={palette.warning.main}
                  />
                ) : (
                  <StatusChip
                    label={DeductionStatusMap[value as keyof typeof DeductionStatusMap]?.label || value}
                    color={DeductionStatusMap[value as keyof typeof DeductionStatusMap]?.color || 'default'}
                  />
                )
              ) : (
                value
              )}
            </TableCell>
          ))}
          <TableCell onClick={(event) => event.stopPropagation()}>
            <DeductionOptionsMenu
              options={getMenuOptions(tableItem.deduction)}
              onClick={(option) => onMenuOptionClick(tableItem.deduction, option)}
            />
          </TableCell>
        </TableRow>
      ))}
      {recoveryLien && (
        <LienRecoveryPopup
          isOpen={!!recoveryLien}
          lien={recoveryLien}
          onSuccess={() => {
            setRecoveryLien(null);
            onRecoverySaved?.();
          }}
          onCancel={() => setRecoveryLien(null)}
        />
      )}
    </>
  );
};

export default DeductionsTableDataRow;
